import moment from 'moment';
import momentDurationFormatSetup from "moment-duration-format";
import { Accordion, AccordionDetails, AccordionSummary, Box, Card, Checkbox, CircularProgress, FormControlLabel, Link, Select, Slider } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffect, useState } from 'react';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Skill } from '../data/Skill';
import { useRootStore } from '../hooks/useRootStore';
import { Person } from '../data/Person';
import { Project } from '../data/Project';
import { Client } from '../data/Client';
import { Range } from '../data/Range';
import { IssueListFilterModel } from './IssueList';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import { AgentType } from '../data/AgentType';
import { UserType } from '../data/UserType';
momentDurationFormatSetup(moment);

export default function IssueListFilter({ initialFilter, onFilterChanged, onFilterClosed }: { initialFilter: IssueListFilterModel, onFilterChanged: (filter: IssueListFilterModel) => void, onFilterClosed: () => void }) {

    const { api, userProfile } = useRootStore();

    //const [selectedStates, setSelectedStates] = useState([] as IssueState[]);
    const [selectedSkills, setSelectedSkills] = useState(initialFilter.skills && initialFilter.skills.length > 0 ? new Map<number, Skill>(initialFilter.skills.map(x => [x.ID, x])) : new Map<number, Skill>());
    const [selectedProjects, setSelectedProjects] = useState(initialFilter.projects && initialFilter.projects.length > 0 ? new Map<number, Project>(initialFilter.projects.map(x => [x.ID, x])) : new Map<number, Project>());
    const [selectedClients, setSelectedClients] = useState(initialFilter.clients && initialFilter.clients.length > 0 ? new Map<number, Client>(initialFilter.clients.map(x => [x.ID, x])) : new Map<number, Client>());
    const [selectedAuthors, setSelectedAuthors] = useState(initialFilter.authors && initialFilter.authors.length > 0 ? new Map<number, Person>(initialFilter.authors.map(x => [x.ID, x])) : new Map<number, Person>());
    const [selectedAssignees, setSelectedAssignees] = useState(initialFilter.assignees && initialFilter.assignees.length > 0 ? new Map<number, Person>(initialFilter.assignees.map(x => [x.ID, x])) : new Map<number, Person>());

    const [employees, setEmployees] = useState(new Map<number, Person>());
    const [skills, setSkills] = useState(new Map<number, Skill>());
    const [projects, setProjects] = useState(new Map<number, Project>());
    const [clients, setClients] = useState(new Map<number, Client>());

    const [rates, setRates] = useState({ from: 0, to: 10 } as Range<number>);
    const [hasFact, setHasFacts] = useState(false);
    const [datesCreate, setDatesCreate] = useState({ from: null, to: null } as Range<Date>);
    const [datesAssigned, setDatesAssigned] = useState({ from: null, to: null } as Range<Date>);
    const [datesDeadline, setDatesDeadline] = useState({ from: null, to: null } as Range<Date>);
    const [datesComplete, setDatesComplete] = useState({ from: null, to: null } as Range<Date>);
    const [datesQueued, setDatesQueued] = useState({ from: null, to: null } as Range<Date>);
    const [datesAcceptance, setDatesAcceptance] = useState({ from: null, to: null } as Range<Date>);

    const [employeesLoading, setEmployeesLoading] = useState(true);
    const [clientsLoading, setClientsLoading] = useState(true);
    const [skillsLoading, setSkillsLoading] = useState(true);

    useEffect(() => {
        if (!clientsLoading && !employeesLoading && !skillsLoading) {
            onFilterChanged({
                states: [],
                skills: Array.from(selectedSkills).map(x => x[1]),
                assignees: Array.from(selectedAssignees).map(x => x[1]),
                authors: Array.from(selectedAuthors).map(x => x[1]),
                clients: Array.from(selectedClients).map(x => x[1]),
                datesAssigned: datesAssigned,
                datesCreate: datesCreate,
                datesQueued: datesQueued,
                isError: false,
                datesCompleted: datesComplete,
                datesAcceptance: datesAcceptance,
                datesDeadline: datesDeadline,
                projects: Array.from(selectedProjects).map(x => x[1]),
                rates: { from: rates.from == 0 ? null : rates.from, to: rates.to },
                hasFact: hasFact,
            })
        }

    }, [datesAssigned, datesQueued, datesComplete, datesAcceptance, datesCreate, datesDeadline, hasFact, rates, selectedAssignees, selectedAuthors, selectedClients, selectedProjects, selectedSkills /*, selectedStates*/]);
    
    useEffect(() => {
        setSelectedAssignees(new Map<number, Person>());
        setSelectedAuthors(new Map<number, Person>());

        api.getEmployees().then(e => {
            setEmployees(new Map(e.map(x => [ x.ID, x ])));
            setEmployeesLoading(false);
        });

        api.getSkills().then(e => {
            setSkills(new Map(e.map(x => [x.ID, x])));
            setSkillsLoading(false);
        });

        api.getClients().then(e => {
            setClients(new Map(e.map(x => [x.ID, x])));
            setProjects(new Map(e.map(x => x.Projects).flat().map(x => [x.ID, x])));
            setClientsLoading(false);
        });

    }, [api, setClients, setProjects]);

    return (
        <Box sx={{ position: 'relative' }}>
            <HighlightOffOutlinedIcon sx={{ position: 'absolute', right: 5, top: -15, cursor: 'pointer' }} onClick={onFilterClosed} />
            <h3 style={{ textAlign: 'left', paddingLeft: 22 }}>Фильтры:</h3>
            <Card>
                <Accordion>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                    >
                        Создана:&nbsp;
                        {!datesCreate.from && !datesCreate.to
                            ? "любая дата" :
                            <span>
                                {`${datesCreate.from ? moment(datesCreate.from).format('DD.MM.YYYY') : ''} - ${datesCreate.to ? moment(datesCreate.to).format('DD.MM.YYYY') : ''}`}
                            </span>
                        }
                    </AccordionSummary>
                    <AccordionDetails>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <Box className="range-wrapper">
                                <DatePicker format="DD.MM.YYYY"
                                    onChange={(e) => setDatesCreate({ from: e?.toDate() ?? null, to: datesCreate.to })}
                                    sx={{ mb: 1, mt: 2, }}
                                    label="Начало"
                                />
                                <DatePicker format="DD.MM.YYYY"
                                    onChange={(e) => setDatesCreate({ from: datesCreate.from, to: e?.toDate() ?? null })}
                                    sx={{ mb: 1, mt: 2 }}
                                    label="Конец"
                                />
                            </Box>
                        </LocalizationProvider>
                    </AccordionDetails>
                </Accordion>
            </Card>
            <Card>
                <Accordion>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                    >
                        Назначена:&nbsp;
                        {!datesAssigned.from && !datesAssigned.to
                            ? "любая дата" :
                            <span>
                                {`${datesAssigned.from ? moment(datesAssigned.from).format('DD.MM.YYYY') : ''} - ${datesAssigned.to ? moment(datesAssigned.to).format('DD.MM.YYYY') : ''}`}
                            </span>
                        }
                    </AccordionSummary>
                    <AccordionDetails>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <Box className="range-wrapper">
                                <DatePicker format="DD.MM.YYYY"
                                    onChange={(e) => setDatesAssigned({ from: e?.toDate() ?? null, to: datesAssigned.to })}
                                    sx={{ mb: 1, mt: 2 }}
                                    label="Начало"
                                />
                                <DatePicker format="DD.MM.YYYY"
                                    onChange={(e) => setDatesAssigned({ from: datesAssigned.from, to: e?.toDate() ?? null })}
                                    sx={{ mb: 1, mt: 2 }}
                                    label="Конец"
                                />
                            </Box>
                        </LocalizationProvider>
                    </AccordionDetails>
                </Accordion>
            </Card>
            <Card>
                <Accordion>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                    >
                        Дедлайн:&nbsp;
                        {!datesDeadline.from && !datesDeadline.to
                            ? "любая дата" :
                            <span>
                                {`${datesDeadline.from ? moment(datesDeadline.from).format('DD.MM.YYYY') : ''} - ${datesDeadline.to ? moment(datesDeadline.to).format('DD.MM.YYYY') : ''}`}
                            </span>
                        }
                    </AccordionSummary>
                    <AccordionDetails>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <Box className="range-wrapper">
                                <DatePicker format="DD.MM.YYYY"
                                    onChange={(e) => setDatesDeadline({ from: e?.toDate() ?? null, to: datesDeadline.to })}
                                    sx={{ mb: 1, mt: 2 }}
                                    label="Начало"
                                />
                                <DatePicker format="DD.MM.YYYY"
                                    onChange={(e) => setDatesDeadline({ from: datesDeadline.from, to: e?.toDate() ?? null })}
                                    sx={{ mb: 1, mt: 2 }}
                                    label="Конец"
                                />
                            </Box>
                        </LocalizationProvider>
                    </AccordionDetails>
                </Accordion>
            </Card>
            <Card>
                <Accordion>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                    >
                        Выполнена:&nbsp;
                        {!datesComplete.from && !datesComplete.to
                            ? "любая дата" :
                            <span>
                                {`${datesComplete.from ? moment(datesComplete.from).format('DD.MM.YYYY') : ''} - ${datesComplete.to ? moment(datesComplete.to).format('DD.MM.YYYY') : ''}`}
                            </span>
                        }
                    </AccordionSummary>
                    <AccordionDetails>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <Box className="range-wrapper">
                                <DatePicker format="DD.MM.YYYY"
                                    onChange={(e) => setDatesComplete({ from: e?.toDate() ?? null, to: datesComplete.to })}
                                    sx={{ mb: 1, mt: 2 }}
                                    label="Начало"
                                />
                                <DatePicker format="DD.MM.YYYY"
                                    onChange={(e) => setDatesComplete({ from: datesComplete.from, to: e?.toDate() ?? null })}
                                    sx={{ mb: 1, mt: 2 }}
                                    label="Конец"
                                />
                            </Box>
                        </LocalizationProvider>
                    </AccordionDetails>
                </Accordion>
            </Card>
            <Card>
                <Accordion>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                    >
                        Принять до:&nbsp;
                        {!datesAcceptance.from && !datesAcceptance.to
                            ? "любая дата" :
                            <span>
                                {`${datesAcceptance.from ? moment(datesAcceptance.from).format('DD.MM.YYYY') : ''} - ${datesAcceptance.to ? moment(datesAcceptance.to).format('DD.MM.YYYY') : ''}`}
                            </span>
                        }
                    </AccordionSummary>
                    <AccordionDetails>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <Box className="range-wrapper">
                                <DatePicker format="DD.MM.YYYY"
                                    onChange={(e) => setDatesAcceptance({ from: e?.toDate() ?? null, to: datesAcceptance.to })}
                                    sx={{ mb: 1, mt: 2 }}
                                    label="Начало"
                                />
                                <DatePicker format="DD.MM.YYYY"
                                    onChange={(e) => setDatesAcceptance({ from: datesAcceptance.from, to: e?.toDate() ?? null })}
                                    sx={{ mb: 1, mt: 2 }}
                                    label="Конец"
                                />
                            </Box>
                        </LocalizationProvider>
                    </AccordionDetails>
                </Accordion>
            </Card>
            <Card>
                <Accordion>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                    >
                        Поставлена в очередь:&nbsp;
                        {!datesQueued.from && !datesQueued.to
                            ? "любая дата" :
                            <span>
                                {`${datesQueued.from ? moment(datesQueued.from).format('DD.MM.YYYY') : ''} - ${datesQueued.to ? moment(datesQueued.to).format('DD.MM.YYYY') : ''}`}
                            </span>
                        }
                    </AccordionSummary>
                    <AccordionDetails>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <Box className="range-wrapper">
                                <DatePicker format="DD.MM.YYYY"
                                    onChange={(e) => setDatesQueued({ from: e?.toDate() ?? null, to: datesQueued.to })}
                                    sx={{ mb: 1, mt: 2 }}
                                    label="Начало"
                                />
                                <DatePicker format="DD.MM.YYYY"
                                    onChange={(e) => setDatesQueued({ from: datesQueued.from, to: e?.toDate() ?? null })}
                                    sx={{ mb: 1, mt: 2 }}
                                    label="Конец"
                                />
                            </Box>
                        </LocalizationProvider>
                    </AccordionDetails>
                </Accordion>
            </Card>
            <Card>
                <Accordion>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                    >
                        <span>
                            <span>Направление:&nbsp;</span>
                            <span>{selectedSkills.size == 0
                                ? "все" :
                                Array.from(selectedSkills).map((a, i) => <>{i == 0 ? '' : ', '}{a[1].Name}</>)
                            }
                            </span>
                        </span>
                    </AccordionSummary>
                    <AccordionDetails>
                        {skillsLoading ? <CircularProgress />
                            : Array.from(skills).map(x =>
                                <Box key={`issue-filter-skill-${x[0]}` }>
                                    <FormControlLabel control={
                                        <Checkbox
                                            value={x[0]}
                                            onChange={(e) => {
                                                const id = Number(e.target.value);
                                                if (e.target.checked) {
                                                    selectedSkills.set(id, skills.get(id)!);
                                                    setSelectedSkills(new Map(selectedSkills));
                                                } else {
                                                    selectedSkills.delete(id);
                                                    setSelectedSkills(new Map(selectedSkills));
                                                }
                                            }}
                                        />}
                                        label={skills.get(Number(x[0]))!.Name}
                                    />
                                </Box>)
                        }
                    </AccordionDetails>
                </Accordion>
            </Card>
            <Card>
                <Accordion>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                    >
                        <span>
                            <span>Проект:&nbsp;</span>
                            <span>{selectedProjects.size == 0
                                ? "все" :
                                Array.from(selectedProjects).map((a, i) => <>{i == 0 ? '' : ', '}<Link href={`projects/${a[0]}`}>{a[1].Name}</Link></>)
                            }
                            </span>
                        </span>
                    </AccordionSummary>
                    <AccordionDetails>
                        {clientsLoading ? <CircularProgress /> : Array.from(projects).map(x =>
                            <Box key={`issue-filter-projects-${x[0]}` }>
                                <FormControlLabel control={
                                    <Checkbox
                                        value={x[0]}
                                        onChange={(e) => {
                                            const id = Number(e.target.value);
                                            if (e.target.checked) {
                                                selectedProjects.set(id, projects.get(id)!);
                                                setSelectedProjects(new Map(selectedProjects));
                                            } else {
                                                selectedProjects.delete(id);
                                                setSelectedProjects(new Map(selectedProjects));
                                            }
                                        }}
                                    />}
                                    label={projects.get(Number(Number(x[0])))!.Name}
                                />
                            </Box>)
                        }
                    </AccordionDetails>
                </Accordion>
            </Card>
            {userProfile?.UserType == UserType.CustomerEmployee ? '' :
                <Card>
                    <Accordion>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="panel1-content"
                            id="panel1-header"
                        >
                            <span>
                                <span>Клиент:&nbsp;</span>
                                <span>{selectedClients.size == 0
                                    ? "все" :
                                    Array.from(selectedClients).map((a, i) => <>{i == 0 ? '' : ', '}<Link href={`clients/${a[0]}`}>{a[1].Name}</Link></>)
                                }
                                </span>
                            </span>
                        </AccordionSummary>
                        <AccordionDetails>
                            {clientsLoading ? <CircularProgress /> :
                                Array.from(clients).map(x =>
                                    <Box key={`issue-filter-clients${x[0]}`}>
                                        <FormControlLabel control={
                                            <Checkbox
                                                value={x[0]}
                                                onChange={(e) => {
                                                    const id = Number(e.target.value);
                                                    if (e.target.checked) {
                                                        selectedClients.set(id, clients.get(id)!);
                                                        setSelectedClients(new Map(selectedClients));
                                                    } else {
                                                        selectedClients.delete(id);
                                                        setSelectedClients(new Map(selectedClients));
                                                    }
                                                }}
                                            />}
                                            label={clients.get(Number(Number(x[0])))!.Name}
                                        />
                                    </Box>)
                            }
                        </AccordionDetails>
                    </Accordion>
                </Card>
            }
            <Card>
                <Accordion>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                    >
                        <span>
                            <span>Автор:&nbsp;</span>
                            <span>{selectedAuthors.size == 0
                                ? "все" :
                                Array.from(selectedAuthors).map((a, i) => <>{i == 0 ? '' : ', '}<Link href={`employees/${a[0]}`}>{a[1].Name}</Link></>)
                            }
                            </span>
                        </span>
                    </AccordionSummary>
                    <AccordionDetails>
                        {employeesLoading ? <CircularProgress /> :
                            <Select
                                id="author-id"
                                multiple
                                value={Array.from(selectedAuthors).map(x => x[0])}
                                fullWidth
                                sx={{p:0}}
                                inputProps={{ size: '15' }}
                                native
                                onChange={(e) => {
                                    const id = Number(e.target.value);
                                    if (selectedAuthors.has(id)) {
                                        selectedAuthors.delete(id);
                                        setSelectedAuthors(new Map(selectedAuthors));
                                    } else {
                                        selectedAuthors.set(id, employees.get(id)!);
                                        setSelectedAuthors(new Map(selectedAuthors));
                                    }
                                }}
                            >
                                {Array.from(employees).sort((a, b) => a[1].Name <= b[1].Name ? -1 : 1).map(e => <option key={'e' + e[0]} value={e[0]}>{e[1].Name}</option>)}
                            </Select>
                        }
                    </AccordionDetails>
                </Accordion>
            </Card>
            <Card>
                <Accordion>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                    >
                        <span>
                            <span>Исполнитель:&nbsp;</span>
                            <span>{selectedAssignees.size == 0
                                ? "не важно" :
                                Array.from(selectedAssignees).map((a, i) => <>{i == 0 ? '' : ', '}<Link href={`employees/${a[0]}`}>{a[1].Name}</Link></>)
                            }
                            </span>
                        </span>
                    </AccordionSummary>
                    <AccordionDetails>
                        {employeesLoading ? <CircularProgress /> :
                            <Select
                                id="author-id"
                                multiple
                                value={Array.from(selectedAssignees).map(x => x[0])}
                                fullWidth
                                inputProps={{ size: '15' }}
                                native
                                onChange={(e) => {
                                    const id = Number(e.target.value);
                                    if (selectedAssignees.has(id)) {
                                        selectedAssignees.delete(id);
                                        setSelectedAssignees(new Map(selectedAssignees));
                                    } else {
                                        selectedAssignees.set(id, employees.get(id)!);
                                        setSelectedAssignees(new Map(selectedAssignees));
                                    }
                                }}
                            >
                                {Array.from(employees).filter(x => x[1].AgentType == AgentType.Employee).sort((a, b) => a[1].Name <= b[1].Name ? -1 : 1).map(e => <option key={'e' + e[0]} value={e[0]}>{e[1].Name}</option>)}
                            </Select>
                        }
                    </AccordionDetails>
                </Accordion>
            </Card>
            <Card>
                <Accordion>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                    >
                        Оценка:&nbsp;
                        {rates.from == 0 && rates.to == 10
                            ? "не важно" :
                            <span>
                                {rates.from == 0 ? ` < ${rates.to}` : `${rates.from} - ${rates.to}`}
                            </span>
                        }
                    </AccordionSummary>
                    <AccordionDetails>
                        <Slider
                            value={[rates.from!, rates.to!]}
                            step={1}
                            min={0}
                            max={10}
                            onChange={(_event: Event, newValue: number | number[]) => setRates({ from: (newValue as number[])[0], to: (newValue as number[])[1] })}
                            valueLabelDisplay="auto"
                            //getAriaValueText={valuetext}
                        />
                    </AccordionDetails>
                </Accordion>
            </Card>
            <Card>
                <Box sx={{ p: 2 }}>С работами:&nbsp;<Checkbox checked={hasFact} onChange={(e) => setHasFacts(e.target.checked)} /></Box>
            </Card>
        </Box>
    )
}